import React from 'react';
import {connect} from 'react-redux';

import {Container} from 'containers/types';

import Page from 'components/Page';
import Loading from 'components/Loading';
import ListContent from 'components/ListContent';
import VisibleIf from 'components/VisibleIf';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import {ConfirmationModal} from 'components/CommonModals';

import {
  fetchOneOffChargeReasons,
  selectOneOffChargeReasons,
  selectOneOffChargeReasonsLoading,
  MODALS,
  deleteOneOffChargeReason
} from 'ducks/oneOffChargeReasons';

import {
  isModalOpen,
  openModal,
  MODALS as BASE_MODALS,
  closeModal
} from 'ducks/modals';

import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import {
  OneOffChargeReasonsTable,
  SubNavigation,
  CreateOneOffChargeReasonModal,
  EditOneOffChargeReasonModal
} from './components';

import './styles.css';

class OneOffChargeReasonsList extends React.Component<
  Container &
    LoginRequiredProps &
    WithPaginationProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  componentDidMount() {
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_ONE_OFF_CHARGE_REASONS,
        locationRef
      },
      () => this.props.fetchOneOffChargeReasons({locationRef})
    );
  }

  onPageChange = ({offset, pageSize}) => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.onPaginationChange({offset, pageSize}, [
      () => fetchOneOffChargeReasons({locationRef})
    ]);
  };

  openEditOneOffChargeReasonModal = ({reason}) => {
    this.props.openModal(MODALS.EDIT_ONE_OFF_CHARGE_REASON, {
      reason
    });
  };

  openConfirmationModal = ({heading, confirmAction, question, ...args}) =>
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading,
      question,
      confirmAction,
      ...args
    });

  deleteOneOffChargeReason = ({reasonId}) => {
    const {match: {params: {locationRef}}} = this.props;

    const extraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () => fetchOneOffChargeReasons({locationRef})
    ];

    this.openConfirmationModal({
      heading: 'Remove charge reason',
      question: 'Are you sure you want to delete this charge reason?',
      confirmAction: () =>
        this.props.deleteOneOffChargeReason(
          {reasonId, locationRef},
          extraActions
        )
    });
  };

  render() {
    const {
      match: {params: {locationRef}},
      oneOffChargeReasons,
      isContentLoading,
      modals: {
        createOneOffChargeReasonModalOpened,
        editOneOffChargeReasonModalOpened,
        confirmationModalOpened
      }
    } = this.props;

    return (
      <Page activeTab="chargeReasons" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_ONE_OFF_CHARGE_REASONS}
          locationRef={locationRef}>
          <ListContent
            className="OneOffChargeReasonsList"
            filters={{}}
            initialFilters={{}}
            navigationExtraElement={SubNavigation}>
            <ListContent.Content>
              {isContentLoading && <Loading />}
              {!isContentLoading && (
                <OneOffChargeReasonsTable
                  reasons={oneOffChargeReasons}
                  pagination={this.props.getPagination()}
                  onPageChange={this.onPageChange}
                  openEditOneOffChargeReasonModal={
                    this.openEditOneOffChargeReasonModal
                  }
                  deleteOneOffChargeReason={this.deleteOneOffChargeReason}
                />
              )}
            </ListContent.Content>
            {createOneOffChargeReasonModalOpened && (
              <CreateOneOffChargeReasonModal locationRef={locationRef} />
            )}
            {editOneOffChargeReasonModalOpened && (
              <EditOneOffChargeReasonModal locationRef={locationRef} />
            )}

            {confirmationModalOpened && <ConfirmationModal />}
          </ListContent>
        </VisibleIf>
      </Page>
    );
  }
}

const mapStateToProps = state => ({
  oneOffChargeReasons: selectOneOffChargeReasons(state),
  isContentLoading: selectOneOffChargeReasonsLoading(state),
  modals: {
    createOneOffChargeReasonModalOpened: isModalOpen(
      state,
      MODALS.CREATE_ONE_OFF_CHARGE_REASON
    ),
    editOneOffChargeReasonModalOpened: isModalOpen(
      state,
      MODALS.EDIT_ONE_OFF_CHARGE_REASON
    ),
    confirmationModalOpened: isModalOpen(state, BASE_MODALS.CONFIRM)
  }
});

const mapDispatchToProps = {
  fetchOneOffChargeReasons,
  openModal,
  deleteOneOffChargeReason,
  requiresPermission
};

const componentWithPagination = withPagination(
  OneOffChargeReasonsList,
  PAGINATION.ONE_OFF_CHARGE_REASONS_LIST,
  {saveToUrl: true}
);

export default connect(mapStateToProps, mapDispatchToProps)(
  LoginRequired(componentWithPagination)
);
